"use client";

import { useEffect } from "react";

/**
 * Scroll-spy for the table of contents rail.
 *
 * Deliberately tiny: the list itself is server rendered, so this component
 * ships no markup. It only observes the headings and flips a data attribute on
 * the already rendered anchors, which CSS styles.
 */
export default function TocScrollSpy({ ids }: { ids: string[] }) {
  useEffect(() => {
    if (!ids.length) return;

    const links = new Map<string, HTMLAnchorElement[]>();
    for (const id of ids) {
      const found = Array.from(
        document.querySelectorAll<HTMLAnchorElement>(`a[data-toc="${CSS.escape(id)}"]`)
      );
      if (found.length) links.set(id, found);
    }
    if (!links.size) return;

    const visible = new Set<string>();

    const paint = () => {
      // The active section is the first heading in document order that is
      // currently on screen; falling back to the last one scrolled past keeps
      // the rail from going blank between headings.
      const active = ids.find((id) => visible.has(id)) ?? lastPassed;
      links.forEach((anchors, id) => {
        for (const a of anchors) a.setAttribute("data-active", String(id === active));
      });
    };

    let lastPassed = ids[0];

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            visible.add(id);
            lastPassed = id;
          } else {
            visible.delete(id);
            if (entry.boundingClientRect.top < 0) lastPassed = id;
          }
        }
        paint();
      },
      { rootMargin: "-88px 0px -65% 0px", threshold: 0 }
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    paint();

    return () => observer.disconnect();
  }, [ids]);

  return null;
}
